import { EntityQuery, LandUse, LandUseArea, LandUseI18N, Query, and, is } from '@the-real-insight/tri-model';

import { userAPI } from '../UserAPI';
import { geometryBounds } from './geometryBounds';

/**
 * The actual land use of an area — the ALKIS "Tatsächliche Nutzung" layer.
 *
 * Read through the generic query endpoint, exactly as the parcels are: the
 * collection is not tenant-scoped, `centerLat`/`centerLon` are indexed as
 * `center_1`, and a `LandUseArea` carries what a map needs. An area belongs to
 * the box by its centre — the same rule the parcels and the tiles follow, and
 * the one the index can answer.
 *
 * This replaces colouring parcels by a `landUse` field, which the MCP tool still
 * does. That answered a different question: it showed ownership boundaries
 * tinted by a guess at what is inside them, while land use has its own geometry.
 * A single plot carries road, greenery and building; one use spans many plots.
 */

/**
 * How many areas one view may draw.
 *
 * The collection holds 30.7 million. Lower than the parcel ceiling because these
 * polygons are larger and more detailed — a `MultiPolygon` of a forest is worth
 * several plot outlines — and because the picture is a coloured surface: past a
 * few hundred shapes the map is a mosaic no one reads.
 */
export const LAND_USE_LIMIT = 800;

export interface LandUseResult {
    areas: any[];
    /** True when the ceiling was reached — what is drawn is part of the area, not all of it. */
    truncated: boolean;
    /** The categories actually present, for a legend that lists what is on screen. */
    categories: LandUse[];
}

/**
 * Colour per category.
 *
 * Taken from the recognition tool's own table so one land use looks the same
 * wherever it is drawn, but keyed on `LandUse` rather than the German wording:
 * the delivered text arrives in three spellings, and a lookup on it loses rows
 * silently. Anything without a colour falls back to the `unknown` grey, which is
 * also what a fifth of the rows genuinely are.
 */
export const LAND_USE_COLORS: Record<string, string> = {
    [LandUse.unknown]: '#808080',
    [LandUse.greenfield]: '#90EE90',
    [LandUse.agriculture]: '#D2B48C',
    [LandUse.brownfield]: '#8B4513',
    [LandUse.forest]: '#228B22',
    [LandUse.water]: '#1E90FF',
    [LandUse.sportsAndEntertainment]: '#FFA07A',
    [LandUse.residential]: '#FFB6C1',
    [LandUse.cemetary]: '#2F4F4F',
    [LandUse.industrialArea]: '#A9A9A9',
    [LandUse.commercialArea]: '#DAA520',
    [LandUse.natureReserve]: '#32CD32',
    [LandUse.dump]: '#A0522D',
    [LandUse.way]: '#DEB887',
    [LandUse.wood]: '#006400',
    [LandUse.watercourse]: '#00BFFF',
    [LandUse.roadTraffic]: '#696969',
    [LandUse.mixedUse]: '#DDA0DD',
    [LandUse.specialPurpose]: '#FFD700',
    [LandUse.square]: '#F0E68C',
    [LandUse.unvegetatedArea]: '#D3D3D3',
    [LandUse.moor]: '#8B4513',
    [LandUse.mining]: '#CD853F',
    [LandUse.trainTraffic]: '#708090',
    [LandUse.swamp]: '#556B2F',
    [LandUse.shippingTraffic]: '#4682B4',
    [LandUse.harborBasin]: '#4169E1',
    [LandUse.airTraffic]: '#87CEEB',
    [LandUse.heath]: '#DAA520',
    [LandUse.sea]: '#000080',
    [LandUse.miningOperations]: '#8B4513',
};

export const landUseColor = (value: string | null | undefined): string =>
    LAND_USE_COLORS[value ?? LandUse.unknown] ?? LAND_USE_COLORS[LandUse.unknown];

export const landUseLabel = (value: string | null | undefined, locale: string): string => {
    const key = locale.startsWith('de') ? 'de' : 'en';
    const table = (LandUseI18N as any)?.[key] ?? {};

    return table[value ?? LandUse.unknown] ?? String(value ?? LandUse.unknown);
};

const areasInBox = async (minLat: number, maxLat: number, minLon: number, maxLon: number): Promise<any[]> => {
    const filter = and(
        is('centerLat').greaterEqual(minLat),
        is('centerLat').lessEqual(maxLat),
        is('centerLon').greaterEqual(minLon),
        is('centerLon').lessEqual(maxLon),
    );

    const rows = await userAPI.query(
        new Query(new EntityQuery(LandUseArea.type, [], undefined, filter)),
        0,
        // One more than the ceiling, so the caller learns the box holds more than
        // it shows without counting first — there is no count endpoint to ask.
        LAND_USE_LIMIT + 1,
    );

    return Array.isArray(rows) ? rows : [];
};

export const loadLandUseForArea = async (
    geometry: any,
    /** Middle of the area. Without one the box's own middle stands in. */
    centre?: { lat: number; lon: number } | null,
): Promise<LandUseResult> => {
    const bounds = geometryBounds(geometry);
    if (!bounds) {
        return { areas: [], truncated: false, categories: [] };
    }

    const [[minLon, minLat], [maxLon, maxLat]] = bounds;
    const rows = await areasInBox(minLat, maxLat, minLon, maxLon);

    if (rows.length <= LAND_USE_LIMIT) {
        return { areas: rows, truncated: false, categories: categoriesOf(rows) };
    }

    /**
     * Too many. The index is scanned by `centerLat` ascending, so what came back
     * is the southern edge of the box over its full width — a band below the area
     * rather than around it. Its height is the useful measurement: it says how
     * much latitude a full load occupies at this density, and a centred window of
     * the same aspect follows from it. Same reasoning as the parcels, and for the
     * same reason — the alternative is drawing a strip nobody asked about.
     */
    const latitudes = rows.map((area: any) => area?.centerLat).filter(Number.isFinite);
    const span = latitudes.length > 1 ? Math.max(...latitudes) - Math.min(...latitudes) : 0;

    const height = maxLat - minLat;
    const width = maxLon - minLon;
    const centreLat = centre?.lat ?? (minLat + maxLat) / 2;
    const centreLon = centre?.lon ?? (minLon + maxLon) / 2;

    const halfLat = span > 0 ? Math.sqrt(span * height) / 2 : height / 4;
    const halfLon = height > 0 ? halfLat * (width / height) : width / 4;

    const banded = await areasInBox(
        Math.max(minLat, centreLat - halfLat),
        Math.min(maxLat, centreLat + halfLat),
        Math.max(minLon, centreLon - halfLon),
        Math.min(maxLon, centreLon + halfLon),
    );

    const areas = banded.slice(0, LAND_USE_LIMIT);

    return { areas, truncated: true, categories: categoriesOf(areas) };
};

/** In the vocabulary's own order, so the legend does not reshuffle between areas. */
const categoriesOf = (areas: any[]): LandUse[] => {
    const present = new Set(areas.map((area: any) => area?.landUse ?? LandUse.unknown));

    return Object.values(LandUse).filter((value) => present.has(value));
};

/** What the map draws for one area: the outline, filled by category. */
export const landUseElement = (area: any, locale: string): any | null => {
    if (!area?.geometry) return null;

    const color = landUseColor(area.landUse);
    const label = landUseLabel(area.landUse, locale);
    const description = [
        label,
        area.nutzart && area.nutzart !== label ? `(${area.nutzart})` : null,
        Number.isFinite(area.flaecheM2) ? `${Math.round(area.flaecheM2).toLocaleString('de-DE')} m²` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return {
        display: {
            type: 'shape',
            shape: area.geometry,
            color,
            fillColor: color,
            // Denser than the parcels — this layer *is* the picture when it is on,
            // where the parcel grid is something to read another picture through.
            // Still short of opaque: the satellite image underneath is what tells
            // you whether the category is plausible.
            fillOpacity: 0.35,
            weight: 0.5,
            opacity: 0.6,
            tooltip: description,
        },
        entity: { name: description, ...area },
    };
};
